import type { GameState } from '../state';
import { D, mul, add, sub, gte, type Decimal } from '../math/num';
import { getTrial, YARD_SLOTS, MAX_YARD_LEVEL, YARD_UPGRADE_SCRAP_MULT, TRIALS, type TrialDef } from '../data/yard';
import { anyPetDef, MAX_PET_LEVEL, petLevel } from '../data/pets';
import { waveScrap } from './spawn';
import { grantPet } from './pets';

/** Trial payout, anchored to frontier income so it never goes stale. */
export function trialScrap(trial: TrialDef, highestWave: number): Decimal {
  return mul(waveScrap(highestWave), D(trial.scrapMult));
}

/** Scrap cost to raise the yard, anchored the same way. */
export function yardUpgradeCost(level: number, highestWave: number): Decimal {
  return mul(waveScrap(highestWave), D(YARD_UPGRADE_SCRAP_MULT * level));
}

/**
 * Training Yard sim (data/yard.ts holds the numbers). Trials resolve off
 * elapsedMs like sorties, so they run identically online and offline.
 */

export interface YardOpResult {
  ok: boolean;
  reason?: string;
}

/** Pets free to train: collected (wave pets AND overlord minis), below the
 *  absolute cap, not deployed, not already enrolled. */
export function benchedPets(state: GameState): string[] {
  const enrolled = new Set(state.yard.trials.map((t) => t.petId));
  return Object.keys(state.pets.collection).filter((id) => {
    const lvl = state.pets.collection[id] ?? 0;
    return !!anyPetDef(id) && lvl > 0 && petLevel(lvl) < MAX_PET_LEVEL && !state.pets.equipped.includes(id) && !enrolled.has(id);
  });
}

/** Trials the current yard level offers. */
export function availableTrials(state: GameState): typeof TRIALS {
  return TRIALS.filter((t) => t.tier <= state.yard.level);
}

export function startTrial(state: GameState, petId: string, trialId: string): YardOpResult {
  const y = state.yard;
  if (!y.unlocked) return { ok: false, reason: 'The yard is not open.' };
  if (y.trials.length >= YARD_SLOTS) return { ok: false, reason: 'Both kennels are busy.' };
  const trial = getTrial(trialId);
  if (!trial) return { ok: false, reason: 'Unknown trial.' };
  if (trial.tier > y.level) return { ok: false, reason: `Needs yard level ${trial.tier}.` };
  if (!anyPetDef(petId) || !(state.pets.collection[petId] ?? 0)) return { ok: false, reason: 'Not collected yet.' };
  if (state.pets.equipped.includes(petId)) return { ok: false, reason: 'Deployed pets are working. Bench it first.' };
  if (y.trials.some((t) => t.petId === petId)) return { ok: false, reason: 'Already enrolled.' };
  const lvl = petLevel(state.pets.collection[petId] ?? 0);
  if (lvl >= MAX_PET_LEVEL) return { ok: false, reason: 'Already a perfect machine.' };
  if (lvl >= trial.trainsTo) return { ok: false, reason: `${trial.name} only trains to level ${trial.trainsTo}. It needs a harder trial.` };

  y.trials.push({ petId, trialId, endsMs: state.elapsedMs + trial.durationMs });
  return { ok: true };
}

/** Number of trials whose timers have run out. */
export function finishedTrials(state: GameState): number {
  return state.yard.trials.filter((t) => state.elapsedMs >= t.endsMs).length;
}

/** Collect every finished trial: +1 level (up to the trial's cap) + Scrap. */
export function collectTrials(state: GameState): number {
  const y = state.yard;
  let collected = 0;
  const keep = [];
  for (const t of y.trials) {
    if (state.elapsedMs < t.endsMs) { keep.push(t); continue; }
    const trial = getTrial(t.trialId);
    if (trial) {
      grantPet(state, t.petId, trial.trainsTo);
      const scrap = trialScrap(trial, state.combat.highestWave);
      state.wallet.scrap = add(state.wallet.scrap, scrap);
      state.meta.lifetimeScrap = add(state.meta.lifetimeScrap, scrap);
      y.trained += 1;
      collected += 1;
    }
  }
  y.trials = keep;
  return collected;
}

export function upgradeYard(state: GameState): YardOpResult {
  const y = state.yard;
  if (!y.unlocked) return { ok: false, reason: 'The yard is not open.' };
  if (y.level >= MAX_YARD_LEVEL) return { ok: false, reason: 'The yard is fully fenced.' };
  const cost = yardUpgradeCost(y.level, state.combat.highestWave);
  if (!gte(state.wallet.scrap, cost)) return { ok: false, reason: 'Not enough Scrap.' };
  state.wallet.scrap = sub(state.wallet.scrap, cost);
  y.level += 1;
  return { ok: true };
}

/** Open the yard — rides the Sparky Boi kill. */
export function unlockYard(state: GameState): void {
  if (state.yard.unlocked) return;
  state.yard.unlocked = true;
  state.meta.unlocks.yard = true;
}
